// VOY-485 (rebuild) — Style + interests (Public profile tab).
//
// DECLARED INPUT. Mirrors the live profile's `StyleInterestsCard` (16-pill
// cloud, multi-select, "used by agents for trip personalization") restyled
// to v6. Functional parity is the test: tap to select/deselect any of the
// 16 options, save persists to `user_preferences.preferences.style_interests`
// via the existing `savePreferences()` helper.
//
// VOY-438's keyword-enrichment migration legacy-maps the old
// `style_interests` to the keyword store (weight=2). New declarations still
// land in their original field — same shape, same writes, just v6 chrome.

import { useState } from 'react'
import {
  STYLE_INTEREST_OPTIONS,
  readStyleInterests,
} from '@/components/profile/helpers/preferences-helpers'
import type { TravelPreferences } from '@/types/profile'

export interface StyleInterestsSectionProps {
  preferences: TravelPreferences
  onSave: (next: TravelPreferences) => Promise<void>
}

export function StyleInterestsSection({ preferences, onSave }: StyleInterestsSectionProps) {
  const currentSelection = readStyleInterests(preferences)
  const [saving, setSaving] = useState(false)

  async function toggle(slug: string) {
    const next = currentSelection.includes(slug)
      ? currentSelection.filter((s) => s !== slug)
      : [...currentSelection, slug]
    setSaving(true)
    try {
      await onSave({ ...preferences, style_interests: next })
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="card" aria-labelledby="style-h">
      <div className="card-h">
        <h3 id="style-h">Style + interests</h3>
        <span className="meta">{saving ? 'SAVING…' : 'TAP TO SELECT'}</span>
      </div>
      <p style={{ color: 'var(--v6-fg-muted)', fontSize: 12.5, margin: '0 0 14px' }}>
        Used by agents for trip personalization. {currentSelection.length} selected.
      </p>
      <div className="pill-cloud" role="group" aria-label="Travel style and interests">
        {STYLE_INTEREST_OPTIONS.map((opt) => {
          const on = currentSelection.includes(opt.value)
          return (
            <button
              key={opt.value}
              type="button"
              className={on ? 'choice-pill on' : 'choice-pill'}
              onClick={() => void toggle(opt.value)}
              aria-pressed={on}
              disabled={saving}
            >
              {opt.label}
            </button>
          )
        })}
      </div>
    </section>
  )
}
